import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import './styles/animations.css';
import { Provider } from 'react-redux';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { HelmetProvider } from 'react-helmet-async';
import App from './App';
import { store } from './redux/store';

const theme = createTheme({
  palette: {
    primary: {
      main: '#14B8A6', // HUSLRZ Teal-500
      light: '#2DD4BF', // Teal-400
      dark: '#0D9488', // Teal-600
      contrastText: '#FFFFFF'
    },
    secondary: {
      main: '#0F766E', // Teal-700
      light: '#14B8A6',
      dark: '#134E4A',
      contrastText: '#FFFFFF'
    },
    success: {
      main: '#10B981' // HUSLRZ Green
    },
    warning: {
      main: '#F59E0B' // HUSLRZ Yellow
    },
    error: {
      main: '#EF4444' // HUSLRZ Red
    },
    info: {
      main: '#3B82F6' // HUSLRZ Blue
    },
    background: {
      default: '#FAFAFA', // HUSLRZ bg-primary
      paper: '#FFFFFF' // HUSLRZ bg-secondary
    },
    text: {
      primary: '#0D3D3A', // HUSLRZ Teal-900
      secondary: '#757575', // HUSLRZ text-secondary
      disabled: '#9E9E9E'
    },
    divider: '#E0E0E0' // HUSLRZ border-primary
  },
  typography: {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    fontSize: 16, // Base font size
    htmlFontSize: 16,
    h1: {
      fontSize: '2.5rem', // 40px - Heading-1
      fontWeight: 700,
      lineHeight: 1.3,
      letterSpacing: '-0.5px',
      color: '#0D3D3A'
    },
    h2: {
      fontSize: '2rem', // 32px - Heading-2
      fontWeight: 600,
      lineHeight: 1.2,
      letterSpacing: '0px',
      color: '#0D3D3A'
    },
    h3: {
      fontSize: '1.75rem', // 28px - Heading-3
      fontWeight: 600,
      lineHeight: 1.4,
      letterSpacing: '0px',
      color: '#0D3D3A'
    },
    h4: {
      fontSize: '1.5rem', // 24px - Heading-4
      fontWeight: 600,
      lineHeight: 1.4,
      color: '#0D3D3A'
    },
    h5: {
      fontSize: '1.125rem', // 18px - Body Large
      fontWeight: 500,
      lineHeight: 1.6,
      color: '#0D3D3A'
    },
    h6: {
      fontSize: '1rem', // 16px - Same as body
      fontWeight: 600,
      lineHeight: 1.4,
      color: '#0D3D3A'
    },
    body1: {
      fontSize: '1rem', // 16px - Body (main text)
      fontWeight: 400,
      lineHeight: 1.6,
      letterSpacing: '0px',
      color: '#0D3D3A'
    },
    body2: {
      fontSize: '0.875rem', // 14px - Body Small
      fontWeight: 400,
      lineHeight: 1.4,
      color: '#757575'
    },
    button: {
      fontSize: '1rem', // 16px - Fixed (prevents zoom on iOS)
      fontWeight: 600,
      lineHeight: 1.4,
      textTransform: 'none',
      letterSpacing: '0px'
    },
    caption: {
      fontSize: '0.75rem', // 12px - Label
      fontWeight: 600,
      lineHeight: 1.5,
      letterSpacing: '0.5px',
      textTransform: 'uppercase',
      color: '#757575'
    },
    subtitle1: {
      fontSize: '1rem', // 16px
      fontWeight: 500,
      lineHeight: 1.6,
      color: '#0D3D3A'
    },
    subtitle2: {
      fontSize: '0.875rem', // 14px
      fontWeight: 600,
      lineHeight: 1.4,
      color: '#0D3D3A'
    },
    overline: {
      fontSize: '0.6875rem', // 11px - Caption
      fontWeight: 400,
      lineHeight: 1.4,
      letterSpacing: '0.5px',
      textTransform: 'uppercase',
      color: '#9E9E9E'
    }
  },
  shape: {
    borderRadius: 12 // HUSLRZ border-radius
  },
  spacing: 4, // 4px grid system
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          padding: '12px 24px', // HUSLRZ button padding
          fontSize: '1rem',
          fontWeight: 600,
          textTransform: 'none',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none'
          }
        },
        contained: {
          '&:hover': {
            boxShadow: '0 2px 8px rgba(20, 184, 166, 0.3)'
          }
        }
      }
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
          }
        }
      }
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            fontSize: '1rem'
          }
        }
      }
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500
        }
      }
    }
  }
});

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);

root.render(
  <React.StrictMode>
    <Provider store={store}>
      <HelmetProvider>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <App />
        </ThemeProvider>
      </HelmetProvider>
    </Provider>
  </React.StrictMode>
);
